import { useTheme } from '../../context/ThemeContext.jsx';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function Toolbar({
  searchQuery, setSearchQuery,
  filterCategory, setFilterCategory,
  filterLicense, setFilterLicense,
  sortBy, sortOrder, onSortChange,
  showActivatedOnly, setShowActivatedOnly,
  compareMode, setCompareMode, comparisonFontIds, setComparisonFontIds, onLaunchComparison,
  viewMode, setViewMode,
  activeLetter, setActiveLetter, letterCounts,
  setPage,
  onShowSmartSearch, onShowPlayground, onShowDuplicates, onShowHealth,
}) {
  const { theme } = useTheme();

  return (
    <>
      {/* Toolbar */}
      <div style={{
        padding: '14px 24px',
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center',
          background: theme.bgInput, borderRadius: 8, padding: '0 12px',
          border: `1px solid ${theme.border}`,
        }}>
          <span style={{ color: theme.textFaint, fontSize: 14, marginRight: 8 }}>⌕</span>
          <input
            type="text"
            placeholder="Search fonts, designers, categories..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              background: 'none', border: 'none', color: theme.text,
              fontSize: 12, padding: '8px 0', width: '100%', outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none', border: 'none', color: theme.textFaint,
                cursor: 'pointer', fontSize: 14, padding: '0 4px',
              }}
            >✕</button>
          )}
        </div>

        <button
          onClick={onShowSmartSearch}
          title="AI Smart Search"
          style={{
            background: 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
            border: 'none', borderRadius: 7,
            color: '#fff', fontSize: 14,
            padding: '7px 12px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            fontFamily: 'inherit', fontWeight: 600,
            boxShadow: '0 2px 8px rgba(124,92,252,0.25)',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>&#10024;</span>
          <span style={{ fontSize: 11 }}>AI</span>
        </button>

        <select
          value={filterCategory}
          onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textSecondary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="All">All Types</option>
          <option value="sans-serif">Sans-Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
          <option value="display">Display</option>
          <option value="handwriting">Handwriting</option>
        </select>

        <select
          value={filterLicense}
          onChange={e => { setFilterLicense(e.target.value); setPage(1); }}
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textSecondary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="All">All Licenses</option>
          <option value="unknown">Unknown</option>
          <option value="commercial">Commercial</option>
          <option value="open-source">Open Source</option>
          <option value="personal">Personal</option>
          <option value="trial">Trial</option>
        </select>

        <select
          value={`${sortBy}:${sortOrder}`}
          onChange={e => onSortChange(e.target.value)}
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textSecondary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <option value="name:ASC">Name A-Z</option>
          <option value="name:DESC">Name Z-A</option>
          <option value="created_at:DESC">Newest First</option>
          <option value="created_at:ASC">Oldest First</option>
          <option value="style_count:DESC">Most Styles</option>
          <option value="style_count:ASC">Fewest Styles</option>
          <option value="total_size:DESC">Largest Size</option>
          <option value="total_size:ASC">Smallest Size</option>
          <option value="rating:DESC">Highest Rated</option>
          <option value="rating:ASC">Lowest Rated</option>
        </select>

        <button
          onClick={() => { setShowActivatedOnly(!showActivatedOnly); setPage(1); }}
          style={{
            background: showActivatedOnly ? theme.greenBg : theme.bgInput,
            border: showActivatedOnly ? `1px solid ${theme.greenBorder}` : `1px solid ${theme.borderMedium}`,
            borderRadius: 7,
            color: showActivatedOnly ? theme.green : theme.textTertiary,
            fontSize: 12, padding: '7px 12px', cursor: 'pointer',
            fontWeight: 500, fontFamily: 'inherit',
          }}
        >Active Only</button>

        <button
          onClick={() => { setCompareMode(!compareMode); if (compareMode) setComparisonFontIds([]); }}
          style={{
            background: compareMode ? theme.accentBg : theme.bgInput,
            border: compareMode ? `1px solid ${theme.accentBorder}` : `1px solid ${theme.borderMedium}`,
            borderRadius: 7,
            color: compareMode ? theme.accent : theme.textTertiary,
            fontSize: 12, padding: '7px 12px', cursor: 'pointer',
            fontWeight: 500, fontFamily: 'inherit',
          }}
        >Compare</button>

        <button
          onClick={onShowPlayground}
          title="CSS Playground"
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textTertiary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >CSS</button>

        <button
          onClick={onShowDuplicates}
          title="Find duplicate fonts"
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textTertiary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >Duplicates</button>

        <button
          onClick={onShowHealth}
          title="Font health dashboard"
          style={{
            background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
            borderRadius: 7, color: theme.textTertiary,
            fontSize: 12, padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >Health</button>

        <div style={{
          display: 'flex', gap: 2,
          background: theme.bgInput, borderRadius: 7, padding: 2,
        }}>
          {['grid', 'list'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                background: viewMode === mode ? theme.borderHover : 'transparent',
                border: 'none', borderRadius: 5,
                color: viewMode === mode ? theme.text : theme.textFaint,
                padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                fontFamily: 'inherit',
              }}
            >{mode === 'grid' ? '⊞' : '☰'}</button>
          ))}
        </div>
      </div>

      {/* Compare bar */}
      {compareMode && (
        <div style={{
          padding: '8px 24px', borderBottom: `1px solid ${theme.borderLight}`,
          display: 'flex', alignItems: 'center', gap: 8,
          background: theme.accentBg,
        }}>
          <span style={{ fontSize: 12, color: theme.accent, fontWeight: 600 }}>
            Compare: {comparisonFontIds.length}/3 selected
          </span>
          {comparisonFontIds.length >= 2 && (
            <button
              onClick={onLaunchComparison}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 600,
                background: 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
                border: 'none', borderRadius: 6, color: '#fff',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Compare Now</button>
          )}
          <button
            onClick={() => { setCompareMode(false); setComparisonFontIds([]); }}
            style={{
              background: 'none', border: 'none', color: theme.textMuted,
              cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
            }}
          >Cancel</button>
        </div>
      )}

      {/* Alphabet Bar */}
      <div style={{
        padding: '8px 24px', borderBottom: `1px solid ${theme.borderLight}`,
        display: 'flex', alignItems: 'center', gap: 2,
        flexWrap: 'nowrap', overflowX: 'auto',
      }}>
        {ALPHABET.map(letter => {
          const count = letterCounts[letter] || 0;
          const isActive = activeLetter === letter;
          return (
            <button
              key={letter}
              onClick={() => { setActiveLetter(isActive ? null : letter); setPage(1); }}
              style={{
                width: 28, height: 26,
                background: isActive ? theme.accentBgHover : 'transparent',
                border: isActive ? `1px solid ${theme.accentBorderHover}` : '1px solid transparent',
                borderRadius: 5,
                color: isActive ? theme.accent : theme.textTertiary,
                fontSize: 11, fontWeight: 600, cursor: count > 0 || isActive ? 'pointer' : 'default',
                opacity: count > 0 || isActive ? 1 : 0.3,
                fontFamily: 'inherit', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.1s',
              }}
              title={`${letter}: ${count} fonts`}
            >{letter}</button>
          );
        })}
        <button
          onClick={() => { setActiveLetter(activeLetter === '#' ? null : '#'); setPage(1); }}
          style={{
            width: 28, height: 26,
            background: activeLetter === '#' ? theme.accentBgHover : 'transparent',
            border: activeLetter === '#' ? `1px solid ${theme.accentBorderHover}` : '1px solid transparent',
            borderRadius: 5,
            color: activeLetter === '#' ? theme.accent : theme.textTertiary,
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title="Non-alphabetic"
        >#</button>
        {activeLetter && (
          <button
            onClick={() => { setActiveLetter(null); setPage(1); }}
            style={{
              marginLeft: 8, background: 'none', border: 'none',
              color: theme.textFaint, cursor: 'pointer',
              fontSize: 11, fontFamily: 'inherit',
            }}
          >Clear</button>
        )}
      </div>
    </>
  );
}
